import type {
  RubricCriterionCount,
  RubricLevel,
  RubricLevelCount,
  RubricPerRoleCriterionCount,
  RubricProjectRole,
  RubricScope,
  RubricTransversalCriterionCount,
} from '../types/rubricDraft';
import type { AiRubricCriterion, AiRubricResponse } from '../constants/rubricLevelPresets';
import { getSubjectById } from '../constants/subjectCatalog';
import { resolveInteractiveRoomUrl } from '@interactive-room';
import type { CourseProfile } from '../types/courseProfile';
import {
  mapCourseProfileValidationError,
  validateCourseLevelAndSubject,
} from '../lib/courseProfileValidation';

function resolveBaseUrl(baseUrl?: string): string {
  return resolveInteractiveRoomUrl(baseUrl);
}

/** Perfil tipado para AI — fail loud si level/subject inválidos; sin inventar 5basico. */
function resolveCourseProfileForAi(course: CourseProfile): Record<string, unknown> {
  const validation = validateCourseLevelAndSubject({
    level: course.level,
    subjectTag: course.subjectTag,
    subjectId: course.subjectId,
    hasCourseContext: true,
    source: 'courseProfile',
    strictAbsent: true,
  });

  if (validation.blocked || validation.code === 'course_profile_invalid') {
    throw new Error(validation.code);
  }

  if (!validation.level || !validation.subjectTag) {
    throw new Error('course_profile_incomplete');
  }

  const STYLES = new Set([
    'visual',
    'kinestesico',
    'auditivo',
    'lectoescritor',
    'colaborativo',
    'individual',
    'gamificado',
    'proyecto',
  ]);

  return {
    id: String(course.id || 'course').trim() || 'course',
    displayName: String(course.displayName || 'Curso').trim() || 'Curso',
    schoolName: course.schoolName ? String(course.schoolName).trim() : undefined,
    subjectId: validation.subjectTag,
    subjectTag: validation.subjectTag,
    additionalSubjectIds: Array.isArray(course.additionalSubjectIds)
      ? course.additionalSubjectIds.map((v) => String(v || '').trim()).filter(Boolean)
      : [],
    level: validation.level,
    interests: Array.isArray(course.interests)
      ? course.interests.map((v) => String(v || '').trim()).filter(Boolean)
      : [],
    learningStyle: Array.isArray(course.learningStyle)
      ? course.learningStyle.map((v) => String(v || '').trim()).filter((v) => STYLES.has(v))
      : [],
    strengths: Array.isArray(course.strengths)
      ? course.strengths.map((v) => String(v || '').trim()).filter(Boolean)
      : [],
    difficulties: Array.isArray(course.difficulties)
      ? course.difficulties.map((v) => String(v || '').trim()).filter(Boolean)
      : [],
  };
}

async function readJsonResponse<T extends Record<string, unknown>>(resp: Response): Promise<T> {
  const contentType = resp.headers.get('content-type') || '';
  const text = await resp.text();

  if (!contentType.includes('application/json')) {
    if (/^\s*</.test(text)) throw new Error('server_endpoint_unavailable');
    throw new Error('invalid_server_response');
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('invalid_server_response');
  }
}

export interface GenerateRubricWithAiParams {
  title: string;
  objective: string;
  evaluationType?: string;
  levels: RubricLevel[];
  levelCount: RubricLevelCount;
  courseProfile: CourseProfile;
  criterionCount?: RubricCriterionCount | number;
  transversalCriterionCount?: RubricTransversalCriterionCount | number;
  perRoleCriterionCount?: RubricPerRoleCriterionCount | number;
  scope?: RubricScope;
  projectRoles?: RubricProjectRole[];
  curriculumTarget?: {
    axis: string;
    oaCode: string;
    oahCode?: string;
    oaaCode?: string;
  } | null;
  oaText?: string;
  oas?: Array<{ code: string; text: string; axis?: string; subjectTag?: string; subjectLabel?: string }>;
  curriculumMode?: 'mono' | 'interdisciplinar' | 'multidisciplinar';
  extraSubjectTags?: string[];
  /** Nombres legibles de asignaturas (curso + extras) para el prompt. */
  subjectLabels?: string[];
  /** Niveles curriculares (diagnóstica multi-nivel). */
  targetLevels?: string[];
  targetLevelLabels?: string[];
  estadoProyecto?: 'inicio' | 'en_curso' | 'casi_terminado';
  avanceDescripcion?: string;
  baseUrl?: string;
}

export interface ExpandRubricCriterionWithAiParams {
  instruction: string;
  criterionName?: string;
  criterionDescription?: string;
  title?: string;
  objective: string;
  evaluationType?: string;
  levels: RubricLevel[];
  scope?: RubricScope;
  projectRoles?: RubricProjectRole[];
  courseProfile: CourseProfile;
  curriculumTarget?: {
    axis: string;
    oaCode: string;
    oahCode?: string;
    oaaCode?: string;
  } | null;
  oaText?: string;
  oas?: Array<{ code: string; text: string; axis?: string }>;
  baseUrl?: string;
}

export interface GenerateRubricWithAiResult {
  rubric: AiRubricResponse;
  provider?: string;
  attempts?: number;
}

export async function generateRubricWithAi(
  params: GenerateRubricWithAiParams,
): Promise<GenerateRubricWithAiResult> {
  const subject = getSubjectById(params.courseProfile.subjectId);
  const trimmedObjective = params.objective.trim();
  if (trimmedObjective.length < 8) {
    throw new Error('objective_too_short');
  }

  const resp = await fetch(`${resolveBaseUrl(params.baseUrl)}/api/studio/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      task: 'rubric',
      userPrompt: trimmedObjective,
      objective: trimmedObjective,
      title: params.title.trim(),
      evaluationType: params.evaluationType || 'formativa',
      levelCount: params.levelCount,
      levels: params.levels,
      criterionCount: params.criterionCount ?? 4,
      transversalCriterionCount: params.transversalCriterionCount ?? 2,
      perRoleCriterionCount: params.perRoleCriterionCount ?? 2,
      scope: params.scope ?? 'individual',
      projectRoles:
        params.scope === 'group' && Array.isArray(params.projectRoles)
          ? params.projectRoles.map((role) => ({
              id: role.id,
              name: role.name,
              description: role.description || '',
            }))
          : [],
      courseProfile: resolveCourseProfileForAi(params.courseProfile),
      ...(params.curriculumTarget?.axis && params.curriculumTarget?.oaCode
        ? { curriculumTarget: params.curriculumTarget }
        : {}),
      ...(params.oaText ? { oaText: params.oaText } : {}),
      ...(params.oas?.length
        ? {
            oas: params.oas.map((oa) => ({
              code: oa.code,
              text: oa.text,
              axis: oa.axis,
              subjectTag: oa.subjectTag,
              subjectLabel: oa.subjectLabel,
            })),
          }
        : {}),
      ...(params.curriculumMode ? { curriculumMode: params.curriculumMode } : {}),
      ...(params.extraSubjectTags?.length
        ? { extraSubjectTags: params.extraSubjectTags }
        : {}),
      ...(params.subjectLabels?.length ? { subjectLabels: params.subjectLabels } : {}),
      ...(params.targetLevels?.length ? { targetLevels: params.targetLevels } : {}),
      ...(params.targetLevelLabels?.length
        ? { targetLevelLabels: params.targetLevelLabels }
        : {}),
      ...(params.estadoProyecto ? { estadoProyecto: params.estadoProyecto } : {}),
      ...(params.avanceDescripcion?.trim()
        ? { avanceDescripcion: params.avanceDescripcion.trim() }
        : {}),
      subjectContext: {
        subjectId: params.courseProfile.subjectId,
        subjectTag: params.courseProfile.subjectTag,
        subjectExpertPrompt: subject?.aiExpertPrompt ?? '',
      },
    }),
  });

  const json = await readJsonResponse<{
    ok?: boolean;
    error?: string;
    rubric?: AiRubricResponse;
    provider?: string;
    attempts?: number;
  }>(resp);

  if (!resp.ok || !json.ok || !json.rubric) {
    throw new Error(json.error || 'generate_rubric_failed');
  }

  return {
    rubric: json.rubric,
    provider: json.provider,
    attempts: json.attempts,
  };
}

export async function expandRubricCriterionWithAi(
  params: ExpandRubricCriterionWithAiParams,
): Promise<{ criterion: AiRubricCriterion; provider?: string; attempts?: number }> {
  const subject = getSubjectById(params.courseProfile.subjectId);
  const instruction = params.instruction.trim();
  if (instruction.length < 4) {
    throw new Error('instruction_too_short');
  }

  const resp = await fetch(`${resolveBaseUrl(params.baseUrl)}/api/studio/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      task: 'rubric_criterion',
      userPrompt: instruction,
      instruction,
      objective: params.objective.trim(),
      title: params.title?.trim() || '',
      evaluationType: params.evaluationType || 'formativa',
      criterionName: params.criterionName?.trim() || '',
      criterionDescription: params.criterionDescription?.trim() || '',
      levels: params.levels,
      scope: params.scope ?? 'individual',
      projectRoles:
        params.scope === 'group' && Array.isArray(params.projectRoles)
          ? params.projectRoles.map((role) => ({
              id: role.id,
              name: role.name,
              description: role.description || '',
            }))
          : [],
      courseProfile: resolveCourseProfileForAi(params.courseProfile),
      ...(params.curriculumTarget?.axis && params.curriculumTarget?.oaCode
        ? { curriculumTarget: params.curriculumTarget }
        : {}),
      ...(params.oaText ? { oaText: params.oaText } : {}),
      ...(params.oas?.length
        ? {
            oas: params.oas.map((oa) => ({
              code: oa.code,
              text: oa.text,
              axis: oa.axis,
            })),
          }
        : {}),
      subjectContext: {
        subjectId: params.courseProfile.subjectId,
        subjectTag: params.courseProfile.subjectTag,
        subjectExpertPrompt: subject?.aiExpertPrompt ?? '',
      },
    }),
  });

  const json = await readJsonResponse<{
    ok?: boolean;
    error?: string;
    criterion?: AiRubricCriterion;
    provider?: string;
    attempts?: number;
  }>(resp);

  if (!resp.ok || !json.ok || !json.criterion) {
    throw new Error(json.error || 'expand_criterion_failed');
  }

  return {
    criterion: json.criterion,
    provider: json.provider,
    attempts: json.attempts,
  };
}

export function mapGenerateRubricError(code: string): string {
  if (code === 'course_profile_invalid' || code === 'course_profile_incomplete') {
    return mapCourseProfileValidationError(code);
  }
  const map: Record<string, string> = {
    objective_too_short: 'Escribe un objetivo de aprendizaje más detallado (mínimo 8 caracteres).',
    instruction_too_short: 'Escribe una instrucción más clara para completar el criterio (mínimo 4 caracteres).',
    objective_required: 'El objetivo de aprendizaje es obligatorio.',
    oa_required: 'Selecciona al menos un Objetivo de Aprendizaje (OA) del currículum antes de generar.',
    mistral_not_configured: 'Mistral no está configurado en el servidor.',
    mistral_auth_failed: 'Error de autenticación con Mistral.',
    generate_rubric_failed: 'No se pudo generar la rúbrica.',
    expand_criterion_failed: 'No se pudo completar el criterio con IA.',
    server_endpoint_unavailable: 'Servidor no disponible. Prueba recargar la página (Ctrl+F5).',
    invalid_server_response: 'Respuesta inválida del servidor. Prueba recargar la página (Ctrl+F5).',
  };
  return map[code] ?? code;
}
